import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import gsap from 'gsap';

import { useNavigationContext } from './NavigationContext';

export type AnimationData = {
    AddAnimations: (animations: Animation[], animationGroupId?: string) => void;
    AddAnimationGroup: (animationGroup: AnimationGroup, animationGroupId?: string) => void;
};

export const AnimationContext = createContext<AnimationData>({
  AddAnimationGroup(): void
  {
    console.error('Animations added to empty method!');
  },
  AddAnimations: () =>
  {
    console.error('Animations added to empty method!');
  }
});

export type Animation = {
    targets: gsap.TweenTarget;
    from?: gsap.TweenVars;
    to?: gsap.TweenVars;
    start: number;
    end: number;
    onUpdate?: () => void;
};

// Duration of animations in an animation group is relative to the group's duration as well as its position.
export type AnimationGroup = {
    id: string,
    animations: (Animation | AnimationGroup)[];
    start: number;
    end: number;
    parentGroup?: AnimationGroup;
    timeline?: gsap.core.Timeline;
    calculatedDuration?: number; // its duration relative to the master timeline.
}

export default function AnimationContextProvider({ children }: JSX.ElementChildrenAttribute): JSX.Element
{
  const mainAnimationGroup: AnimationGroup = useMemo(() =>
  {
    const timeline = gsap.timeline();
    timeline.pause();
    return {
      id: 'main_timeline',
      start: 0,
      end: 1,
      animations: [],
      timeline,
      calculatedDuration: 1
    };
  }, []);
    // a value between 0 and 1
  const { scrollValue } = useNavigationContext();
  // when we're adding animations, we need to make sure that the timeline updates in order to incorporate these new
  // animations
  const [ animationsVersion, setAnimationsVersion ] = useState(0);

  const invalidateAnimations = useCallback(() => {
    const { timeline } = mainAnimationGroup;
    if (!timeline) return;
    timeline.invalidate();
    // force update of the complete timeline, even if new animations have been added in
    const time = timeline.time();
    timeline.seek(time - 0.01).seek(0).seek(time);

    setAnimationsVersion(old => { return old + 1;});
  }, [mainAnimationGroup]);

  const AddAnimation = useCallback((animation: Animation, animationGroupId: string) =>
  {
    const parentAnimationGroup = GetAnimationGroupById(mainAnimationGroup, animationGroupId);
    if (!parentAnimationGroup) {
      throw `Animation group with id '${animationGroupId}' couldn't be found.`;
    }
    if (!parentAnimationGroup.timeline) {
      throw `Animation group '${animationGroupId}' does not have a timeline.`;
    }

    parentAnimationGroup.animations.push(animation);
    parentAnimationGroup.timeline.invalidate();
    AddAnimationToTimeline(animation, parentAnimationGroup);

    invalidateAnimations();
  }, [invalidateAnimations, mainAnimationGroup]);

  const AddAnimationGroup = useCallback((animationGroup: AnimationGroup, animationGroupId?: string) =>
  {
    const parentAnimationGroup = GetAnimationGroupById(mainAnimationGroup, animationGroupId || 'main_timeline');

    if (!parentAnimationGroup) {
      throw `Animation group with id ${animationGroupId} couldn't be found.`;
    }

    if (GetAnimationGroupById(mainAnimationGroup, animationGroup.id)) {
      throw `Animation group with id ${animationGroup.id} was already created.`;
    }

    animationGroup.parentGroup = parentAnimationGroup;
    parentAnimationGroup.animations.push(animationGroup);
    if (parentAnimationGroup.timeline)
      parentAnimationGroup.timeline.invalidate();

    InitGroupTimeline(animationGroup);

    invalidateAnimations();
  }, [invalidateAnimations, mainAnimationGroup]);

  const AddAnimations = useCallback((animations: Animation[], animationGroupId: string = mainAnimationGroup.id) =>
  {
    animations.forEach(animation =>
    {
      AddAnimation(animation as Animation, animationGroupId);
    });

    invalidateAnimations();

  }, [AddAnimation, invalidateAnimations, mainAnimationGroup.id]);

  useEffect(() =>
  {
    if (mainAnimationGroup.timeline) {
      mainAnimationGroup.timeline.seek(scrollValue);
    }
  }, [ mainAnimationGroup.timeline, scrollValue, animationsVersion ]);

  return (
    <AnimationContext.Provider
      value={{
        AddAnimations,
        AddAnimationGroup
      }}
    >
      {children}
    </AnimationContext.Provider>
  );
}

export const useAnimationContext = (): AnimationData => useContext(AnimationContext);

function AddAnimationToTimeline(animation: Animation, parentAnimationGroup: AnimationGroup)
{
  if (!parentAnimationGroup.timeline)
    throw `Animation group '${parentAnimationGroup.id}' doesnt have a timeline.`;
  if (parentAnimationGroup.calculatedDuration === null || parentAnimationGroup.calculatedDuration === undefined)
    throw `calculatedDuration of the animation group '${parentAnimationGroup.id}' is undefined.`;
  const childPosition = animation.start * parentAnimationGroup.calculatedDuration;
  const childDuration = (animation.end - animation.start) * parentAnimationGroup.calculatedDuration;
  if (animation.from && animation.to)
    parentAnimationGroup.timeline.fromTo(animation.targets, animation.from, {
      ...animation.to,
      onUpdate: animation.onUpdate,
      duration: childDuration,
      immediateRender: false,
    }, childPosition);
  else if (animation.from) {
    parentAnimationGroup.timeline.from(animation.targets, {
      ...animation.from,
      onUpdate: animation.onUpdate,
      duration: childDuration,
      immediateRender: false,
    }, childPosition);
  }
  else if (animation.to) {
    parentAnimationGroup.timeline.to(animation.targets, {
      ...animation.to,
      onUpdate: animation.onUpdate,
      duration: childDuration,
      immediateRender: false,
    }, childPosition);
  }
}

function InitGroupTimeline(animationGroup: AnimationGroup)
{
  if (!animationGroup.parentGroup)
    throw `Animation group ${animationGroup.id} doesnt have a parent.`;
  if (!animationGroup.parentGroup.timeline)
    throw `Animation group ${animationGroup.parentGroup.id} doesnt have a timeline.`;
  if (!animationGroup.parentGroup.calculatedDuration)
    throw `calculatedDuration of the animation group '${animationGroup.parentGroup.id}' is undefined.`;
  if (!animationGroup.timeline) {
    animationGroup.timeline = gsap.timeline();
    animationGroup.parentGroup.timeline.add(animationGroup.timeline, animationGroup.start * animationGroup.parentGroup.calculatedDuration);
  }
  const groupDuration = animationGroup.end - animationGroup.start;
  animationGroup.calculatedDuration = animationGroup.parentGroup.calculatedDuration * groupDuration;
  animationGroup.animations.forEach(animation =>
  {
    if ((animation as AnimationGroup).id) {
      animation = animation as AnimationGroup;
      animation.parentGroup = animationGroup;
      InitGroupTimeline(animation);
    }
    else {
      AddAnimationToTimeline(animation as Animation, animationGroup);
    }
  });
}

function GetAnimationGroupById(animationGroup: AnimationGroup, animationGroupId: string): AnimationGroup | null
{
  if (animationGroup.id === animationGroupId)
    return animationGroup;
  for (const child of animationGroup.animations) {
    const childGroup = (child as AnimationGroup);
    if (childGroup.id) {
      const result = GetAnimationGroupById(childGroup, animationGroupId);
      if (result)
        return result;
    }
  }
  return null;
}